import type { Conversation } from '@/features/conversations/conversations.api';

/**
 * Client-side MIRROR of the Hub's `q=` search semantics (`list_conversations`
 * in `hub/app/api/conversations.py` — see the `q` paragraph of its docstring
 * and the "Search" section of Contract C).
 *
 * The server is the real filter — every fetched page already matches `q`.
 * This predicate exists for ONE reason: the realtime projector
 * (`projectMessageNewIntoLists` in `@/lib/realtime/projection`) writes
 * new-message rows into every cached conversations list, and its membership
 * gate checks account scope and `view` but NOT `search` — so a non-matching
 * row can be injected into a searched list's cache between refetches.
 * `ConversationList` guard-filters the flattened pages through this predicate
 * to hide those rows.
 *
 * Because it re-filters rows the SERVER returned, its semantics must mirror
 * the backend exactly — a stricter guard would wrongly hide server hits (the
 * pre-`q=` raw-substring guard hid a `+6013...` row when the operator typed
 * `0136 139`). Mirrored rules:
 *
 *   - case-insensitive SUBSTRING over `display_name` / `phone_e164` /
 *     `peer_id`, OR-combined;
 *   - when the query contains at least one digit, the phone/peer legs match
 *     its DIGITS-ONLY form (numbers are stored as `+60...` E.164 while agents
 *     type local `0`-prefixed forms with spaces/dashes — `"0136 139 981"`
 *     strips to a substring of `"+60136139981"`); the name leg always matches
 *     the raw trimmed text;
 *   - `%` / `_` / `\` are literals (`String.includes` gives us that for free;
 *     the server escapes them out of ILIKE);
 *   - a whitespace-only query matches everything (the server treats it as
 *     absent).
 *
 * The Hub's DB test suite (`hub/tests/test_conversations_search.py`) and this
 * module's vitest suite assert the same cases on the same inputs, so a drift
 * between the two implementations turns one of them red.
 */
export function matchesSearch(
  contact: Conversation['contact'],
  search: string,
): boolean {
  const raw = search.trim().toLowerCase();
  if (!raw) return true;
  const digits = raw.replace(/\D/g, '');
  // The number legs' needle: digits-only when the query has any digit, the
  // raw text otherwise — byte-for-byte the backend's `search_digits` rule.
  const numberNeedle = digits !== '' ? digits : raw;
  return (
    (contact.display_name ?? '').toLowerCase().includes(raw) ||
    (contact.phone_e164 ?? '').toLowerCase().includes(numberNeedle) ||
    contact.peer_id.toLowerCase().includes(numberNeedle)
  );
}
